import { useState } from 'react';
import { useNavigate, Link } from '@tanstack/react-router';
import { ArrowLeft, Copy, Check } from 'lucide-react';
import { Button } from '@jeraisonne/ui';
import { SCHOOL_LEVELS, SUBJECTS } from '@jeraisonne/db';
import type { SchoolLevel, SubjectSlug } from '@jeraisonne/db';
import { useAuth } from '@/lib/auth';
import { useCreateClass } from '@/lib/tutor/mutations';

export function TutorClassNewPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const createClass = useCreateClass();

  const [name, setName] = useState('');
  const [level, setLevel] = useState<SchoolLevel | ''>('');
  const [subject, setSubject] = useState<SubjectSlug | ''>('');
  const [description, setDescription] = useState('');
  const [copied, setCopied] = useState(false);
  const [createdInviteCode, setCreatedInviteCode] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!user || !name.trim()) return;
    const result = await createClass.mutateAsync({
      tutorId: user.id,
      name: name.trim(),
      level: level || null,
      subject: subject || null,
      description: description.trim() || undefined,
    });
    setCreatedInviteCode(result.invite_code);
    setTimeout(
      () => navigate({ to: '/tutor/classes/$classId', params: { classId: result.id } }),
      1500,
    );
  }

  async function copyCode() {
    if (!createdInviteCode) return;
    await navigator.clipboard.writeText(createdInviteCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 sm:py-8">
      {/* Back */}
      <Link
        to="/tutor/classes"
        className="font-display text-ink-500 hover:text-ink-900 mb-6 inline-flex items-center gap-1.5 text-sm font-semibold transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Mes classes
      </Link>

      <div className="mb-6">
        <p className="font-display text-accent-500 text-[11px] font-semibold uppercase tracking-[.14em]">
          Nouvelle classe
        </p>
        <h1 className="font-display text-ink-900 mt-1 text-2xl font-extrabold tracking-tight md:text-[26px]">
          Créer une classe
        </h1>
        <p className="text-ink-500 mt-1 font-sans text-sm">
          Un code d'invitation sera généré automatiquement.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="border-ink-200 flex flex-col gap-4 rounded-2xl border bg-white p-6"
        >
          <FormField label="Nom de la classe" required>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex : 4ème B, Cours particulier Léa…"
              className="border-ink-200 bg-ink-50 text-ink-900 placeholder:text-ink-400 focus:border-primary-500 h-11 w-full rounded-xl border px-3.5 font-sans text-sm transition-colors focus:bg-white focus:outline-none"
              required
            />
          </FormField>

          <FormField label="Niveau">
            <select
              value={level}
              onChange={(e) => setLevel(e.target.value as SchoolLevel | '')}
              className="border-ink-200 bg-ink-50 text-ink-900 focus:border-primary-500 h-11 w-full appearance-none rounded-xl border px-3.5 font-sans text-sm transition-colors focus:outline-none"
            >
              <option value="">— Choisir un niveau —</option>
              {SCHOOL_LEVELS.map((l) => (
                <option key={l.value} value={l.value}>
                  {l.label}
                </option>
              ))}
            </select>
          </FormField>

          <FormField label="Matière principale">
            <select
              value={subject}
              onChange={(e) => setSubject(e.target.value as SubjectSlug | '')}
              className="border-ink-200 bg-ink-50 text-ink-900 focus:border-primary-500 h-11 w-full appearance-none rounded-xl border px-3.5 font-sans text-sm transition-colors focus:outline-none"
            >
              <option value="">— Choisir une matière —</option>
              {SUBJECTS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </FormField>

          <FormField label="Description (optionnel)">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="Notes internes sur cette classe…"
              className="border-ink-200 bg-ink-50 text-ink-900 placeholder:text-ink-400 focus:border-primary-500 w-full resize-none rounded-xl border px-3.5 py-3 font-sans text-sm transition-colors focus:bg-white focus:outline-none"
            />
          </FormField>

          {createClass.error && (
            <p className="text-error-500 bg-error-50 rounded-xl px-3 py-2 font-sans text-sm">
              {createClass.error.message}
            </p>
          )}

          {/* Mobile : action principale pleine largeur, en premier dans le DOM. */}
          <div className="flex flex-col gap-2 pt-1 sm:flex-row-reverse sm:justify-start">
            <Button
              type="submit"
              loading={createClass.isPending}
              disabled={!name.trim()}
              className="min-h-[2.75rem] w-full sm:w-auto"
            >
              Créer la classe
            </Button>
            <Link
              to="/tutor/classes"
              className="border-ink-200 text-ink-700 font-display hover:border-ink-300 inline-flex min-h-[2.75rem] items-center justify-center rounded-xl border px-4 text-sm font-bold transition-colors"
            >
              Annuler
            </Link>
          </div>
        </form>

        {/* Code d'invitation — affiché seulement une fois la classe créée */}
        <div>
          {createdInviteCode && (
            <div className="from-primary-700 to-primary-900 relative overflow-hidden rounded-2xl bg-gradient-to-br p-7 text-white">
              <svg
                className="absolute inset-0 h-full w-full opacity-10"
                viewBox="0 0 400 300"
                preserveAspectRatio="none"
              >
                <g fill="none" stroke="white" strokeWidth="1">
                  {[60, 110, 160, 210, 260].map((y) => (
                    <path key={y} d={`M0 ${y} Q100 ${y - 30} 200 ${y} T400 ${y}`} />
                  ))}
                </g>
              </svg>
              <div className="relative">
                <p className="font-display text-accent-300 text-[11px] font-semibold uppercase tracking-[.14em]">
                  Classe créée · Code d'invitation
                </p>
                <p className="mt-3 font-mono text-4xl font-semibold tracking-[.04em]">
                  {createdInviteCode}
                </p>
                <p className="mt-2 font-sans text-sm leading-relaxed text-white/75">
                  Tes élèves entrent ce code dans l'app pour rejoindre la classe.
                </p>
                <div className="mt-4 flex gap-2">
                  <button
                    onClick={copyCode}
                    className="font-display inline-flex h-9 items-center gap-1.5 rounded-xl bg-white/15 px-3.5 text-sm font-bold text-white transition-colors hover:bg-white/25"
                  >
                    {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                    {copied ? 'Copié !' : 'Copier'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FormField({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="font-display text-ink-700 mb-1.5 block text-xs font-semibold">
        {label}
        {required && <span className="text-accent-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}
